import React, { useState, useMemo, useEffect } from 'react';
import { Mail, Users, TrendingUp, Send } from 'lucide-react';
import EmailCard from './AuxEmail.jsx/EmailCard';
import EmailModal from './AuxEmail.jsx/EmailModal';
import { Pagination } from '@mui/material';
import { Banner } from '../../../common/Banner';
import { getAllEmails } from '../../../../services/mailService';
import { PageLoader } from '../../../loader/index';
import { ErrorPage } from '../../../loader/index';
import MonthlyModal from './AuxEmail.jsx/MonthlyModal';
import { trySendBulkLogMails } from '../../../../services/quotasService';
import EmailTestModal from './AuxEmail.jsx/EmailTestModal';
const getMonthName = (monthString) => {
  const [year, month] = monthString.split('-');
  return new Date(year, month - 1).toLocaleDateString('es-ES', { month: 'long', year: 'numeric' });
};

// Componente de Estadísticas
const StatsCard = ({ title, value, icon: Icon, gradient }) => (
  <div className={`${gradient} p-6 rounded-xl shadow-lg`}>
    <div className="flex items-center justify-between">
      <div>
        <p className="text-sm opacity-90 mb-1">{title}</p>
        <p className="text-2xl font-bold">{value}</p>
      </div>
      <Icon className="w-8 h-8 opacity-80" />
    </div>
  </div>
);

// Componente de Filtros
const Filters = ({ filters, setFilters, options, onClear, setShowModalMonthly, setShowModalTest }) => (
  <div className="mb-6 space-y-4">
    <div className="flex flex-col sm:flex-row gap-4">
      <select
        value={filters.type}
        onChange={(e) => setFilters(prev => ({ ...prev, type: e.target.value }))}
        className="sm:w-60 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0488AF] focus:border-transparent"
      >
        <option value="">Todos los tipos</option>
        {options.types.map(type => (
          <option key={type} value={type}>{type.charAt(0).toUpperCase() + type.slice(1)}</option>
        ))}
      </select>

      <select
        value={filters.month}
        onChange={(e) => setFilters(prev => ({ ...prev, month: e.target.value }))}
        className="sm:w-60 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0488AF] focus:border-transparent"
      >
        <option value="">Todos los meses</option>
        {options.months.map(month => (
          <option key={month} value={month}>{getMonthName(month)}</option>
        ))}
      </select>

      <select
        value={filters.year}
        onChange={(e) => setFilters(prev => ({ ...prev, year: e.target.value }))}
        className="sm:w-48 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0488AF] focus:border-transparent"
      >
        <option value="">Todos los años</option>
        {options.years.map(year => (
          <option key={year} value={year}>{year}</option>
        ))}
      </select>

      <button className='bg-[#0488AF] text-white px-4 py-2 rounded-lg hover:bg-[#0488AF]/90 transition-all duration-200' onClick={() => setShowModalMonthly(true)}> 
        Historial
      </button>
      <button className='bg-[#0488AF] text-white px-4 py-2 rounded-lg hover:bg-[#0488AF]/90 transition-all duration-200' onClick={()=> setShowModalTest(true)}>
        <span>Enviar correos de prueba</span>
      </button>

    </div>

    {(filters.type || filters.month || filters.year) && (
      <button onClick={onClear} className="text-sm text-[#0488AF] hover:text-[#09274A] font-medium">
        Limpiar filtros
      </button>
    )}
  </div>
);
// Componente Principal
const EmailViewAdmin = () => {
  const [emails, setEmails] = useState([]);
  const [filters, setFilters] = useState({ type: '', month: '', year: '' });
  const [currentPage, setCurrentPage] = useState(1);
  const [expandedEmail, setExpandedEmail] = useState(null);
  const [selectedEmail, setSelectedEmail] = useState(null);
  const [page, setPage] = useState(1);
  const itemsPerPage = 10; // Correos por pagina
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null); 
  const [showModalMonthly, setShowModalMonthly] = useState(false);
  const [showModalTest, setShowModalTest] = useState(false);
  const loadEmails = async () => {
    try {
        const response = await getAllEmails();
        if (response.error) {
            console.error(response.message);
            setError( "Error al cargar los correos.");
            setEmails([]);
        } else {
            setEmails((response.emails || []).map(email => ({
                ...email,
                to: email.to.split(',').map(addr => addr.trim()) // asegúrate que siempre sea array
            })));
        }
    } catch (error) {
        console.error("Error loading emails:", error);
        setEmails([]); 
        setError("Hubo un error al cargar los correos. Por favor, inténtalo de nuevo más tarde.");
    } finally {
        setLoading(false); 
    }
    };


  useEffect(() => {
    loadEmails();
  }, []);

  // Opciones para filtros
  const filterOptions = useMemo(() => ({
    types: [...new Set(emails.map(email => email.type).filter(Boolean))],
    months: [...new Set(emails.map(email => {
      const date = new Date(email.createdAt);
      return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
    }))].sort().reverse(),
    years: [...new Set(emails.map(email => new Date(email.createdAt).getFullYear()))].sort().reverse()
  }), [emails]);

  // Emails filtrados
  const filteredEmails = useMemo(() => {
    return emails.filter(email => {
      const date = new Date(email.createdAt);
      const emailMonth = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
      
      return (
        (!filters.type || email.type === filters.type) &&
        (!filters.month || emailMonth === filters.month) &&
        (!filters.year || date.getFullYear() === parseInt(filters.year))
      );
    }).sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
  }, [emails, filters]);

  // Estadísticas
  const statistics = useMemo(() => {
    const totalRecipients = filteredEmails.reduce((acc, email) => acc + email.to.length, 0);
    const typesCount = {};
    
    filteredEmails.forEach(email => {
      if (email.type) typesCount[email.type] = (typesCount[email.type] || 0) + 1;
    });

    const mostUsedType = Object.keys(typesCount).reduce((a, b) => 
      typesCount[a] > typesCount[b] ? a : b, Object.keys(typesCount)[0]
    );

    return {
      totalEmails: filteredEmails.length,
      totalRecipients,
      mostUsedType: mostUsedType || 'N/A',
      typesCount: Object.keys(typesCount).length
    };
  }, [filteredEmails]);

  // Calcular total de páginas
  const pageCount = Math.ceil(filteredEmails.length / itemsPerPage);

  // Obtener los correos de la página actual
  const paginatedEmails = filteredEmails.slice((page - 1) * itemsPerPage, page * itemsPerPage);


  const clearFilters = () => {
    setFilters({ type: '', month: '', year: '' });
    setCurrentPage(1);
  };

  if (loading) return <PageLoader />;
  if (error) return <ErrorPage message={error} ></ErrorPage>;
  return (
    <main className="w-full overflow-x-hidden overflow-y-auto bg-white rounded-xl shadow-md p-4 md:p-6">
      <Banner title="Historial de Correos" message={"Revisa todos los correos enviados por el sistema"}/>

      {/* Estadísticas */}
      <div className="mb-6 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatsCard 
          title="Total Correos" 
          value={statistics.totalEmails} 
          icon={Mail} 
          gradient="bg-gradient-to-r from-[#09274A] to-[#0488AF] text-white" 
        />
        <StatsCard 
          title="Destinatarios" 
          value={statistics.totalRecipients} 
          icon={Users} 
          gradient="bg-gradient-to-r from-[#0488AF] to-[#09274A] text-white" 
        />
        <StatsCard 
          title="Tipos Únicos" 
          value={statistics.typesCount} 
          icon={TrendingUp} 
          gradient="bg-white border-2 border-[#0488AF] text-[#0488AF]" 
        />
        <StatsCard 
          title="Tipo Popular" 
          value={statistics.mostUsedType} 
          icon={Send} 
          gradient="bg-white border-2 border-[#09274A] text-[#09274A]" 
        />
      </div>

      {/* Filtros */}
      <Filters 
        filters={filters} 
        setFilters={setFilters} 
        options={filterOptions} 
        onClear={clearFilters}
        setShowModalMonthly={setShowModalMonthly}
        setShowModalTest={setShowModalTest}
      />

      {/* Lista de correos */}
      <div className="space-y-3">
        {paginatedEmails.map((email) => (
          <EmailCard
            key={email.id}
            email={email}
            expanded={expandedEmail === email.id}
            onToggleExpand={() => setExpandedEmail(expandedEmail === email.id ? null : email.id)}
            onViewDetails={setSelectedEmail}
          />
        ))}
      </div>

      {/* Mensaje cuando no hay resultados */}
      {filteredEmails.length === 0 && (
        <div className="text-center py-12">
          <Mail className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-[#09274A] mb-2">No se encontraron correos</h3>
          <p className="text-gray-600">
            {filters.type || filters.month || filters.year
              ? 'No hay correos que coincidan con los filtros aplicados.'
              : 'Aún no se han enviado correos.'}
          </p>
        </div>
      )}

      {/* Paginación */}
    {pageCount > 1 && (
        <div className="flex justify-center mt-4 md:mt-5">
            <Pagination
                count={pageCount}
                page={page}
                onChange={(event, value) => setPage(value)}
                color="primary"
                size="small"
                siblingCount={0}
                className="scale-90 md:scale-100"
            />
        </div>
    )}    

      {/* Modal de detalles */}
      {selectedEmail && (
        <EmailModal email={selectedEmail} onClose={() => setSelectedEmail(null)} />
      )}

      {/* Modal de correos mensuales */}
      {showModalMonthly && (
        <MonthlyModal
          onClose={() => setShowModalMonthly(false)}
        />
      )}

      {/* Modal de correos de prueba */}
      {showModalTest && (
        <EmailTestModal
          isOpen={showModalTest}  
          onClose={() => setShowModalTest(false)}
        />
      )}
    </main>
  );
};

export default EmailViewAdmin;